/**
 * Time range filter — the one range control for the admin dashboard.
 *
 * A row of separated pills (Today · 3D · 7D · 14D · 30D · 90D · Custom) plus an
 * optional refresh button. Separated pills, not a ToggleButtonGroup: the group fuses
 * its children's corners into one bar, and the design calls for individually rounded
 * pills.
 *
 * Colour comes entirely from the MUI theme — `color="primary"` is the app's deep teal
 * via `theme/muiTheme.ts`. There is not a single hex value in this file, and there
 * must never be one: a screen that wants a different accent should change the theme,
 * not this control.
 *
 * Controlled component. Pair it with `useTimeRangeParam` to persist the selection in
 * the URL; pass `presets` to render an alternate vocabulary (HOUR_PRESETS for the
 * monitors, LOG_PRESETS for the log viewer).
 */

import React, { useMemo, useState } from 'react';
import { Box, Button, IconButton, Popover, Stack, TextField, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  DAY_PRESETS,
  TimeRangePreset,
  TimeRangeToken,
  TimeRangeValue,
  formatCustomLabel,
  isValidYmd,
  resolveRangeYmd,
} from './timeRange';

export interface TimeRangeFilterProps<T extends string = TimeRangeToken> {
  value: TimeRangeValue<T>;
  onChange: (next: TimeRangeValue<T>) => void;
  /** Renders a refresh button when supplied. Omit it and no button appears. */
  onRefresh?: () => void;
  /** Spins the refresh icon. */
  refreshing?: boolean;
  disabled?: boolean;
  /** Defaults to the canonical six. */
  presets?: TimeRangePreset[];
  /** Drops the Custom pill — for scales where an arbitrary span makes no sense. */
  hideCustom?: boolean;
  size?: 'small' | 'medium';
}

/** Shared pill shape. Everything visual that is not a colour lives here. */
const pillSx = {
  borderRadius: 999,
  textTransform: 'none',
  minWidth: 0,
  px: 1.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
} as const;

function TimeRangeFilter<T extends string = TimeRangeToken>({
  value,
  onChange,
  onRefresh,
  refreshing = false,
  disabled = false,
  presets = DAY_PRESETS,
  hideCustom = false,
  size = 'small',
}: TimeRangeFilterProps<T>): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');

  const isCustom = value.type === 'custom';
  const customLabel = useMemo(
    () => (isCustom ? formatCustomLabel(value as { from: string; to: string }) : 'Custom'),
    [isCustom, value]
  );

  const openCustom = (event: React.MouseEvent<HTMLElement>) => {
    // Seed the fields with whatever is currently showing, so opening Custom while
    // "30D" is active starts from those same two dates instead of blank inputs.
    const seed = resolveRangeYmd(value as TimeRangeValue<string>);
    setDraftFrom(isCustom ? (value as { from: string }).from : seed.from);
    setDraftTo(isCustom ? (value as { to: string }).to : seed.to);
    setAnchorEl(event.currentTarget);
  };

  const closeCustom = () => setAnchorEl(null);

  const applyDisabled =
    !isValidYmd(draftFrom) || !isValidYmd(draftTo) || draftFrom > draftTo;

  const applyCustom = () => {
    if (applyDisabled) return;
    onChange({ type: 'custom', from: draftFrom, to: draftTo });
    setAnchorEl(null);
  };

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      role="group"
      aria-label="Time range"
    >
      {presets.map((preset) => {
        const active = value.type === preset.token;
        return (
          <Button
            key={preset.token}
            size={size}
            color="primary"
            variant={active ? 'contained' : 'outlined'}
            disableElevation
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange({ type: preset.token as T })}
            sx={pillSx}
          >
            {preset.label}
          </Button>
        );
      })}

      {!hideCustom && (
        <Button
          size={size}
          color="primary"
          variant={isCustom ? 'contained' : 'outlined'}
          disableElevation
          disabled={disabled}
          aria-pressed={isCustom}
          aria-haspopup="dialog"
          onClick={openCustom}
          sx={pillSx}
        >
          {customLabel}
        </Button>
      )}

      {onRefresh && (
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size={size}
              color="primary"
              aria-label="Refresh"
              disabled={disabled || refreshing}
              onClick={onRefresh}
              sx={{
                '@keyframes timeRangeRefreshSpin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            >
              <RefreshIcon
                fontSize={size === 'medium' ? 'medium' : 'small'}
                sx={
                  refreshing
                    ? { animation: 'timeRangeRefreshSpin 1s linear infinite' }
                    : undefined
                }
              />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeCustom}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, borderRadius: 2 } } }}
      >
        <Stack spacing={2} sx={{ minWidth: 260 }}>
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="From"
              type="date"
              size="small"
              fullWidth
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              fullWidth
              value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>Both dates are inclusive.</Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={closeCustom} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disableElevation
              disabled={applyDisabled}
              onClick={applyCustom}
              sx={{ ...pillSx, px: 2 }}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
}

export default TimeRangeFilter;
